import Cookies from "./useCookies";
import api from "@/services/api";

interface RefreshResponse {
  accessToken: string;
  message?: string;
}

class TokenRefreshManager {
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (token: string) => void;
    reject: (error: Error) => void;
  }> = [];

  private processQueue(error: Error | null = null, token: string | null = null) {
    this.failedQueue.forEach((promise) => {
      if (error) {
        promise.reject(error);
      } else {
        promise.resolve(token!);
      }
    });

    this.failedQueue = [];
  }

  async refreshAccessToken(): Promise<string> {
    if (this.isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        this.failedQueue.push({ resolve, reject });
      });
    }

    this.isRefreshing = true;

    try {
      const response = await api.post<RefreshResponse>(
        "/auth/refresh",
        {},
        {
          withCredentials: true,
        }
      );

      const { accessToken } = response.data;

      Cookies.setSecureCookie("accessToken", accessToken, 1, true);

      this.processQueue(null, accessToken);
      this.isRefreshing = false;

      return accessToken;
    } catch (error) {
      this.processQueue(error instanceof Error ? error : new Error(String(error)), null);
      this.isRefreshing = false;

      Cookies.deleteCookie("accessToken");

      // Don't navigate here — callers (route guards) already know the
      // current path/query (e.g. `redirectTo`) and decide where to send
      // the user. A hard redirect to a bare "/login" here drops that
      // context and breaks the mobile deep-link handoff.
      throw error;
    }
  }

  isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const expirationTime = payload.exp * 1000;
      const currentTime = Date.now();

      const bufferTime = 5 * 60 * 1000;

      return expirationTime - currentTime < bufferTime;
    } catch (error) {
      console.error("Error parsing token:", error);
      return true;
    }
  }

  async getValidAccessToken(): Promise<string | null> {
    const accessToken = await Cookies.getCookie<string>("accessToken");

    if (!accessToken) {
      return null;
    }

    if (this.isTokenExpired(accessToken)) {
      try {
        return await this.refreshAccessToken();
      } catch (error) {
        console.error("Failed to refresh token:", error);
        return null;
      }
    }

    return accessToken;
  }

  clearAuth() {
    Cookies.deleteCookie("accessToken");
    this.failedQueue = [];
    this.isRefreshing = false;
  }
}

export const tokenRefreshManager = new TokenRefreshManager();
